import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, ExternalLink, ChevronRight, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import api from '../lib/api';

/**
 * HelpTooltip Component
 * Provides contextual help tooltips linked to SOP documentation
 * 
 * Usage:
 * <HelpTooltip moduleId="leave-management" />
 * <HelpTooltip moduleId="employee-onboarding" showLabel />
 */
export const HelpTooltip = ({ 
  moduleId, 
  showLabel = false, 
  size = 'default',
  className = '' 
}) => {
  const [helpData, setHelpData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchHelp = useCallback(async () => {
    if (!moduleId || helpData) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/help/context/${moduleId}`);
      setHelpData(response.data);
    } catch (error) {
      console.error('Failed to fetch help:', error);
    } finally {
      setLoading(false);
    }
  }, [moduleId, helpData]);

  useEffect(() => {
    if (isOpen && !helpData) {
      fetchHelp();
    }
  }, [isOpen, helpData, fetchHelp]);

  const sizeClasses = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const iconSize = sizeClasses[size] || sizeClasses.default;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`p-1 h-auto text-slate-400 hover:text-blue-600 ${className}`}
          data-testid={`help-${moduleId}`}
        >
          <HelpCircle className={iconSize} />
          {showLabel && <span className="ml-1 text-xs">Help</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : helpData ? (
          <div className="p-4">
            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-blue-600" />
              {helpData.title}
            </h4>
            <p className="text-sm text-slate-600 mb-3">
              {helpData.quick_help}
            </p>
            
            {helpData.key_points && helpData.key_points.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-slate-500 mb-1.5">Key Points:</p>
                <ul className="space-y-1">
                  {helpData.key_points.slice(0, 4).map((point, idx) => (
                    <li key={idx} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <ChevronRight className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="pt-2 border-t flex items-center justify-between">
              <a 
                href={helpData.full_sop_link}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                View Full SOP
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-slate-500">
            Help not available for this section
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * HelpSearchDialog Component
 * Global help search dialog
 */
export const HelpSearchDialog = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchHelp = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/help/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchHelp(query);
    }, 300);

    return () => clearTimeout(debounce);
  }, [query, searchHelp]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            Search Help
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search help topics..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-2">
              {results.map((result) => (
                <a
                  key={result.module_id}
                  href={`/sop-documents`}
                  className="block p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                >
                  <h4 className="font-medium text-slate-900 text-sm">
                    {result.title}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {result.quick_help}
                  </p>
                </a>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <div className="py-8 text-center text-slate-500 text-sm">
              No results found for "{query}"
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 text-sm">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * QuickTip Component
 * Shows a quick tip inline
 */
export const QuickTip = ({ tipId, className = '' }) => {
  const [tip, setTip] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchTip = async () => {
      try {
        const response = await api.get(`/help/tip/${tipId}`);
        setTip(response.data);
      } catch (error) {
        console.error('Failed to fetch tip:', error);
      }
    };

    if (tipId) {
      fetchTip();
    }
  }, [tipId]);

  if (!tip || !isVisible) return null;

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">{tip.title}</p>
            <p className="text-xs text-blue-700 mt-0.5">{tip.content}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="p-1 h-auto text-blue-400 hover:text-blue-600"
          onClick={() => setIsVisible(false)}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default HelpTooltip;
